import { Component, computed, inject } from '@angular/core';
import { NgStyle } from '@angular/common';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { CorporateDesignService } from '../../../../services/corporate-design/corporate-design.service';
import { OnDemandSelectionStateService } from '../../../../services/on-demand/on-demand-selection-state.service';
import {
  EEntityType,
  ERelationType,
  EFahrgasttyp,
  IOnDemandPassengerDataForm,
} from '@web/dtos';
import { ERoute } from '../../../../constants/routes.enum';
import { SalutationStore } from '../../../../services/api/salutation/salutation-store.service';
import { LanguagePipe } from '../../../../pipes/language/language.pipe';
import { FilterTextsPipe } from '../../../../pipes/filterTexts/filterTexts.pipe';
import { WebTextStore } from '../../../../services/api/text/web-text-store.service';
import { UserStore } from '../../../../services/api/user/user/user-store.service';
import { OnDemandTicketConfigurationSummaryComponent } from '../on-demand-ticket-configuration-summary/on-demand-ticket-configuration-summary.component';
import { CheckboxComponent } from '../../../../components/data-input/form-inputs/checkbox/checkbox.component';
import { DropDownComponent } from '../../../../components/data-input/form-inputs/drop-down/drop-down.component';
import { TextInputComponent } from '../../../../components/data-input/form-inputs/text-input/text-input.component';
import { DateInputComponent } from '../../../../components/data-input/form-inputs/date-input/date-input/date-input.component';
import { ButtonComponent } from '../../../../components/feedback/button/button.component';
import {
  formatDateOnly,
  fromInputDate,
  parseIsoDate,
  toInputDate,
} from '@web/shared-functions/date';

@Component({
  selector: 'aw-on-demand-ticket-configuration-passenger-data',
  templateUrl: './on-demand-ticket-configuration-passenger-data.component.html',
  imports: [
    NgStyle,
    ReactiveFormsModule,
    LanguagePipe,
    FilterTextsPipe,
    OnDemandTicketConfigurationSummaryComponent,
    CheckboxComponent,
    DropDownComponent,
    TextInputComponent,
    DateInputComponent,
    ButtonComponent,
  ],
})
export class OnDemandTicketConfigurationPassengerDataComponent {
  protected readonly ERelationType = ERelationType;
  protected readonly EEntityType = EEntityType;

  protected readonly corporateDesign = inject(CorporateDesignService);
  protected readonly selectionState = inject(OnDemandSelectionStateService);
  protected readonly textStore = inject(WebTextStore);
  protected readonly userStore = inject(UserStore);
  protected readonly salutationStore = inject(SalutationStore);
  private readonly formBuilder = inject(FormBuilder);
  private readonly languagePipe = inject(LanguagePipe);
  private readonly router = inject(Router);

  protected readonly ERoute = ERoute;
  protected readonly salutationOptions = computed(() =>
    this.salutationStore.salutations().map(salutation => ({
      id: String(salutation.id),
      name: this.languagePipe.transform(salutation.texts),
    }))
  );
  protected readonly passengerForms: FormArray<
    FormGroup<IOnDemandPassengerDataForm>
  > = this.initPassengerForms();
  protected readonly expandedStates = Array.from(
    { length: this.passengerForms.length },
    () => true
  );

  private initPassengerForms(): FormArray<
    FormGroup<IOnDemandPassengerDataForm>
  > {
    const count = this.selectionState.passengerCount();
    const existing = this.selectionState.allPassengersPersonalData();
    const defaultSalutationId = String(
      this.salutationStore.salutations()[0]?.id ?? ''
    );
    const forms: FormGroup<IOnDemandPassengerDataForm>[] = [];

    for (let index = 0; index < Math.max(count, 1); index++) {
      forms.push(
        this.formBuilder.group<IOnDemandPassengerDataForm>({
          ticketForMe: this.formBuilder.nonNullable.control(false),
          salutation: this.formBuilder.nonNullable.control(
            existing[index]?.salutation ?? defaultSalutationId,
            Validators.required
          ),
          firstName: this.formBuilder.nonNullable.control(
            existing[index]?.firstName ?? '',
            Validators.required
          ),
          lastName: this.formBuilder.nonNullable.control(
            existing[index]?.lastName ?? '',
            Validators.required
          ),
          birthDate: this.formBuilder.nonNullable.control<Date | undefined>(
            this.parseDisplayDate(existing[index]?.birthDate),
            Validators.required
          ),
        })
      );
    }

    return this.formBuilder.array(forms);
  }

  protected checkTicketForMe(index: number, checked: boolean): void {
    const passengerForm = this.passengerForms.at(index);
    const defaultSalutationId = String(
      this.salutationStore.salutations()[0]?.id ?? ''
    );

    if (!checked) {
      passengerForm.patchValue({
        salutation: defaultSalutationId,
        firstName: '',
        lastName: '',
        birthDate: undefined,
      });
      return;
    }

    const user = this.userStore.user();
    passengerForm.patchValue({
      salutation: this.salutationStore.getId(
        user?.salutation,
        defaultSalutationId
      ),
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      birthDate: parseIsoDate(user?.birthDate),
    });
  }

  protected getPassengerFahrgasttyp(index: number): string {
    return (
      this.selectionState.passengerTicketSelections()[index]?.fahrgasttyp ??
      EFahrgasttyp.ERWACHSENER
    );
  }

  protected togglePassenger(index: number): void {
    this.expandedStates[index] = !this.expandedStates[index];
  }

  protected navigateNext(): void {
    if (this.passengerForms.invalid) {
      this.passengerForms.markAllAsTouched();
      return;
    }

    const passengers = this.passengerForms.getRawValue().map(passenger => ({
      salutation: passenger.salutation,
      firstName: passenger.firstName,
      lastName: passenger.lastName,
      birthDate: this.formatDisplayDate(passenger.birthDate),
    }));

    this.selectionState.allPassengersPersonalData.set(passengers);
    this.selectionState.passengerData.set(passengers[0]);
    this.router
      .navigate([`/${ERoute.onDemandTicketConfiguration}/${ERoute.payment}`])
      .catch(err => console.error('Navigation failed', err));
  }

  private parseDisplayDate(value: string | undefined): Date | undefined {
    return value ? parseIsoDate(toInputDate(value)) : undefined;
  }

  private formatDisplayDate(value: Date | undefined): string {
    const inputDate = formatDateOnly(value);
    return inputDate ? fromInputDate(inputDate) : '';
  }
}
